import React, { useState, useRef, useEffect } from 'react';
import { X, Printer, Download, FileText, Loader, AlertTriangle } from 'lucide-react';
import apiService from '../config';
import { jsPDF } from 'jspdf';


const CompensationNotices = ({
  ownerId = null, // Can be a specific ID or the string "all"
  selectedDistrict = '',
  selectedTaluka = '',
  selectedVillage = '',
  currentProjectName = '',
  currentProjectId = '',
  currentProposalId = '',
  isVisible = false,
  // proposalData = {},
  onClose,
}) => {
  const [noticeData, setNoticeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // For printing
  const [isLoadingData, setIsLoadingData] = useState(false); // For fetching data
  const [error, setError] = useState(null);

  const printRef = useRef(null);
  const [usersLAO, setUsersLAO] = useState([]);
  const[proposalData, setProposalData]  = useState({})

  // Edit
  // State for scheduling modal
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  // Fetch data when the component becomes visible or the ownerId changes
  // Replace the entire useEffect that fetches data
  useEffect(() => {
    console.log('🔄 useEffect triggered with:', {
      isVisible,
      ownerId,
      currentProjectId,
      currentProposalId
    });
    fetchData();
  }, [isVisible, ownerId, currentProjectId, currentProposalId]);

  const fetchData = async () => {

    if (!ownerId) {
      console.log('❌ No ownerId provided, skipping API call');
      setNoticeData([]);
      return;
    }

    setIsLoadingData(true);
    setError(null);
    setNoticeData([]); // Clear previous data

    try {
      let response;
      if (ownerId === 'all') {

        const projectId =
          currentProjectId ||
          localStorage.getItem("currentProjectId") ||
          JSON.parse(localStorage.getItem("selectedProposal"))?.project_id;

        const proposalId =
          currentProposalId ||
          localStorage.getItem("currentProposalId") ||
          JSON.parse(localStorage.getItem("selectedProposal"))?.acquisition_proposal_id;

        if (!projectId || !proposalId) {
          throw new Error('Project ID and Proposal ID are required.');
        }

        response = await apiService.getOwnerCompensation(
          null,
          null,
          null,
          Number(projectId),
          Number(proposalId)
        );


        if (response.status === 200) {
          let ownerDataArray = [];

          // Check if data is already an array
          if (Array.isArray(response.data)) {
            ownerDataArray = response.data;
          }
          // Check if data is an object with an array inside (common API pattern)
          else if (response.data && typeof response.data === 'object') {
            // Try common property names that might contain the array
            if (Array.isArray(response.data.data)) {
              ownerDataArray = response.data.data;
            } else if (Array.isArray(response.data.owners)) {
              ownerDataArray = response.data.owners;
            } else if (Array.isArray(response.data.results)) {
              ownerDataArray = response.data.results;
            } else if (Array.isArray(response.data.records)) {
              ownerDataArray = response.data.records;
            } else {
              // If it's a single object, wrap it in an array
              console.log('⚠️ Response is a single object, wrapping in array');
              ownerDataArray = [response.data];
            }
          }

          setNoticeData(ownerDataArray);
        } else {
          console.log('Unexpected response format:', response);
          throw new Error("Failed to fetch owner data.");
        }
      } else {
        console.log('Calling API for SINGLE owner:', ownerId);

        // API call for a single owner
        response = await apiService.get(`getOwnerCompensation?ownerId=${ownerId}`);


        if (response.status === 200 && response.data && response.compensation) {
          const mergedData = { ...response.data, ...response.compensation };
          setNoticeData([mergedData]);
        } else {
          console.log('Invalid response for single owner:', response);
          throw new Error('Owner data not found or invalid response from server.');
        }
      }
    } catch (err) {
      console.error('API call failed:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        stack: err.stack
      });
      setError(`Failed to load data for the notice. ${err.message}`);
      setNoticeData([]);
    } finally {
      console.log('🏁 API call completed, setting loading to false');
      setIsLoadingData(false);
    }
  };

  // Fetch LAO user details
  useEffect(() => {
    const fetchUsersLAO = async () => {
      if (!isVisible || !currentProjectId || !currentProposalId) return;
      try {
        const response = await apiService.getAssignLAO(3, currentProjectId, currentProposalId);
        const proposalresponse = await apiService.getProposalById(currentProjectId,currentProposalId)
        console.log("Prop res" , proposalresponse);
        setProposalData(proposalresponse?.data)

        
        if (response.status === 200 && response.data) {
          setUsersLAO(Array.isArray(response.data) ? response.data : []);
        } else {
          setUsersLAO([]);
        }
      } catch (error) {
        console.error('Error fetching LAO users:', error);
        setUsersLAO([]);
      }
    };
    fetchUsersLAO();
  }, [isVisible, currentProjectId, currentProposalId]);

  console.log("LAO", usersLAO);

  // Edit
  // Initialize schedule data when modal opens
  useEffect(() => {
    if (isScheduleModalOpen && noticeData.length > 0) {
      // Pre-fill with existing data or default values
      const initialData = noticeData.map(owner => ({
        land_owner_id: owner.land_owner_id,
        owner_name: owner.owner_name,
        survey_no: owner.survey_no,
        scheduled_date: owner.scheduled_date || '',
        scheduled_time: owner.scheduled_time || ''
      }));
      setScheduleData(initialData);
    }
  }, [isScheduleModalOpen, noticeData]);

  // Calculates total values directly from the raw API data stored in noticeData
  const getCompensationValue = (key) => {
    if (!noticeData || noticeData.length === 0) return 0;

    const sumByKey = (k) => noticeData.reduce((sum, record) => sum + (Number(record[k]) || 0), 0);
    const isIndividual = noticeData.length === 1;

    if (isIndividual) {
      const record = noticeData[0];
      switch (key) {
        case 'landRate': return record.land_rate || 0;
        case 'landMultiplied': return record.land_amount_multiplied || 0;
        case 'multipliedLandRate': return record.land_amount || 0;
        case 'treeCompensation': return record.tree_amount || 0;
        case 'forestTreeCompensation': return record.forest_tree_amount || 0;
        case 'homeCompensation': return record.home_amount || 0;
        case 'cropCompensation': return record.crop_amount || 0;
        case 'waterBodyCompensation': return record.water_body_amount || 0;
        case 'tinshedCompensation': return record.tinshed_amount || 0;
        case 'wireCompensation': return record.wire_amount || 0;
        case 'wellAndStructure': return (record.structure_amount || 0) + (record.borewell_amount || 0);
        case 'otherCompensation': return record.other_asset_amount || 0;
        case 'pipelineCompensation': return record.pipeline_amount || 0;
        case 'interestAward': return record.interest_amount || 0;
        case 'additionalCompensation': return record.additional_compensation || 0;
        case 'totalFinalAward': return record.total_compensation || 0;
        case 'govtRecovery': return record.govt_recovery || 0;
        case 'advanceAmount': return record.advance_amount || 0;
        case 'advanceInterestAmount': return record.advance_payment_interest_amount || 0;
        case 'totalAcquiredArea': return record.owner_aqr_area || 0;
        case 'solatiumAmount': return record.solatium_compensation_amount || 0;
        case 'bothInterestTotal':
          return Number(getCompensationValue('interestAward') || 0) + Number(getCompensationValue('advanceInterestAmount') || 0);

        case 'no1':
          return Number(getCompensationValue('landRate') || 0) * Number(getCompensationValue('totalAcquiredArea') || 0);

        case 'no4': // Total Trees
          return (record.tree_amount || 0) + (record.forest_tree_amount || 0);

        // All other structures/assets
        case 'no5':
          return (record.home_amount || 0) + (record.well_amount || 0) + (record.crop_amount || 0) + (record.water_body_amount || 0) + (record.tinshed_amount || 0) + (record.wire_amount || 0) + (record.structure_amount || 0) + (record.borewell_amount || 0) + (record.other_asset_amount || 0);

        case 'no6':
          return Number(getCompensationValue('multipliedLandRate') || 0)
            // + Number(getCompensationValue('solatiumAmount') || 0)
            + Number(getCompensationValue('no4') || 0)
            + Number(getCompensationValue('no5') || 0);

        case 'no10':
          return Number(getCompensationValue('no6') || 0)
            + Number(getCompensationValue('solatiumAmount') || 0)
            + Number(getCompensationValue('bothInterestTotal') || 0)
            + Number(getCompensationValue('pipelineCompensation') || 0);

        case 'no11':
          return Number(getCompensationValue('advanceAmount') || 0)
            + Number(getCompensationValue('govtRecovery') || 0);

        // Net Payable Amount
        case 'totalFinalCompensation':
          return Number(getCompensationValue('no10') || 0)
            - Number(getCompensationValue('no11') || 0);

        default: return 0;
      }
    } else { // Multiple Owners
      switch (key) {
        case 'landRate': return 0; // Summing rates is meaningless
        case 'landMultiplied': return 0; // Summing multipliers is meaningless
        case 'multipliedLandRate': return sumByKey('land_amount');
        case 'treeCompensation': return sumByKey('tree_amount');
        case 'forestTreeCompensation': return sumByKey('forest_tree_amount');
        case 'homeCompensation': return sumByKey('home_amount');
        case 'cropCompensation': return sumByKey('crop_amount');
        case 'waterBodyCompensation': return sumByKey('water_body_amount');
        case 'tinshedCompensation': return sumByKey('tinshed_amount');
        case 'wireCompensation': return sumByKey('wire_amount');
        case 'wellAndStructure': return sumByKey('structure_amount') + sumByKey('borewell_amount');
        case 'otherCompensation': return sumByKey('other_asset_amount');
        case 'pipelineCompensation': return sumByKey('pipeline_amount');
        case 'interestAward': return sumByKey('interest_amount');
        case 'additionalCompensation': return sumByKey('additional_compensation');
        case 'totalFinalAward': return sumByKey('total_compensation');
        case 'govtRecovery': return sumByKey('govt_recovery');
        case 'advanceAmount': return sumByKey('advance_amount');
        case 'advanceInterestAmount': return sumByKey('advance_payment_interest_amount');
        case 'totalAcquiredArea': return sumByKey('owner_aqr_area');

        case 'bothInterestTotal':
          return Number(getCompensationValue('interestAward') || 0)
            + Number(getCompensationValue('advanceInterestAmount') || 0);

        case 'no4': // Total Trees
          return (getCompensationValue('treeCompensation') || 0)
            + (getCompensationValue('forestTreeCompensation') || 0);

        case 'no5': // All other structures/assets
          return (getCompensationValue('homeCompensation') || 0)
            + (getCompensationValue('cropCompensation') || 0)
            + (getCompensationValue('waterBodyCompensation') || 0)
            + (getCompensationValue('tinshedCompensation') || 0)
            + (getCompensationValue('wireCompensation') || 0)
            + (getCompensationValue('wellAndStructure') || 0)
            + (getCompensationValue('otherCompensation') || 0);

        case 'no6':
          return Number(getCompensationValue('multipliedLandRate') || 0)
            + Number(getCompensationValue('no4') || 0)
            + Number(getCompensationValue('no5') || 0);

        case 'no10':
          return Number(getCompensationValue('no6') || 0) + Number(getCompensationValue('no6') || 0)
            + Number(getCompensationValue('bothInterestTotal') || 0)
            + Number(getCompensationValue('pipelineCompensation') || 0);

        case 'no11':
          return Number(getCompensationValue('advanceAmount') || 0)
            + Number(getCompensationValue('govtRecovery') || 0);

        // Net Payable Amount
        case 'totalFinalCompensation':
          return Number(getCompensationValue('no10') || 0)
            - Number(getCompensationValue('no11') || 0);

        default: return 0;
      }
    }
  };

  const getCompensationValueForOwner = (ownerRecord, key) => {
    if (!ownerRecord) return 0;

    switch (key) {
      case 'landRate': return ownerRecord.land_rate || 0;
      case 'landMultiplied': return ownerRecord.land_amount_multiplied || 0;
      case 'multipliedLandRate': return ownerRecord.land_amount || 0;
      case 'treeCompensation': return ownerRecord.tree_amount || 0;
      case 'forestTreeCompensation': return ownerRecord.forest_tree_amount || 0;
      case 'homeCompensation': return ownerRecord.home_amount || 0;
      case 'cropCompensation': return ownerRecord.crop_amount || 0;
      case 'waterBodyCompensation': return ownerRecord.water_body_amount || 0;
      case 'tinshedCompensation': return ownerRecord.tinshed_amount || 0;
      case 'wireCompensation': return ownerRecord.wire_amount || 0;
      case 'wellAndStructure': return (ownerRecord.structure_amount || 0) + (ownerRecord.borewell_amount || 0);
      case 'otherCompensation': return ownerRecord.other_asset_amount || 0;
      case 'pipelineCompensation': return ownerRecord.pipeline_amount || 0;
      case 'interestAward': return ownerRecord.interest_amount || 0;
      case 'additionalCompensation': return ownerRecord.additional_compensation || 0;
      case 'totalFinalAward': return ownerRecord.total_compensation || 0;
      case 'govtRecovery': return ownerRecord.govt_recovery || 0;
      case 'advanceAmount': return ownerRecord.advance_amount || 0;
      case 'advanceInterestAmount': return ownerRecord.advance_payment_interest_amount || 0;
      case 'totalAcquiredArea': return ownerRecord.owner_aqr_area || 0;
      case 'solatiumAmount': return ownerRecord.solatium_compensation_amount || 0;

      // Calculated values for individual owner
      case 'bothInterestTotal':
        return Number(getCompensationValueForOwner(ownerRecord, 'interestAward') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'advanceInterestAmount') || 0);

      case 'no1':
        return Number(getCompensationValueForOwner(ownerRecord, 'landRate') || 0) *
          Number(getCompensationValueForOwner(ownerRecord, 'totalAcquiredArea') || 0);

      case 'no4': // Total Trees
        return (ownerRecord.tree_amount || 0) + (ownerRecord.forest_tree_amount || 0);

      // All other structures/assets
      case 'no5':
        return (ownerRecord.home_amount || 0) + (ownerRecord.well_amount || 0) +
          (ownerRecord.crop_amount || 0) + (ownerRecord.water_body_amount || 0) +
          (ownerRecord.tinshed_amount || 0) + (ownerRecord.wire_amount || 0) +
          (ownerRecord.structure_amount || 0) + (ownerRecord.borewell_amount || 0) +
          (ownerRecord.other_asset_amount || 0);

      case 'no6':
        return Number(getCompensationValueForOwner(ownerRecord, 'multipliedLandRate') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'no4') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'no5') || 0);

      case 'no10':
        return Number(getCompensationValueForOwner(ownerRecord, 'no6') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'solatiumAmount') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'bothInterestTotal') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'pipelineCompensation') || 0);

      case 'no11':
        return Number(getCompensationValueForOwner(ownerRecord, 'advanceAmount') || 0) +
          Number(getCompensationValueForOwner(ownerRecord, 'govtRecovery') || 0);

      // Net Payable Amount
      case 'totalFinalCompensation':
        return Number(getCompensationValueForOwner(ownerRecord, 'no10') || 0) -
          Number(getCompensationValueForOwner(ownerRecord, 'no11') || 0);

      default: return 0;
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('hi-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const numberToMarathiWords = (num) => {
    if (!num || isNaN(num)) return "शून्य";
    const number = Math.round(num); // Ensure we are working with an integer

    const ones = ["", "एक", "दोन", "तीन", "चार", "पाच", "सहा", "सात", "आठ", "नऊ", "दहा", "अकरा", "बारा", "तेरा", "चौदा", "पंधरा", "सोळा", "सतरा", "अठरा", "एकोणीस"];
    const tens = ["", "", "वीस", "तीस", "चाळीस", "पन्नास", "साठ", "सत्तर", "ऐंशी", "नव्वद"];

    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "");
      if (n < 1000) return ones[Math.floor(n / 100)] + "शे " + (n % 100 > 0 ? numToWords(n % 100) : "");
      return "";
    };

    let result = "";
    if (Math.floor(number / 10000000) > 0) {
      result += numToWords(Math.floor(number / 10000000)) + " कोटी ";
    }
    if (Math.floor((number / 100000) % 100) > 0) {
      result += numToWords(Math.floor((number / 100000) % 100)) + " लाख ";
    }
    if (Math.floor((number / 1000) % 100) > 0) {
      result += numToWords(Math.floor((number / 1000) % 100)) + " हजार ";
    }
    if (Math.floor((number / 100) % 10) > 0) {
      result += numToWords(Math.floor((number / 100) % 10) * 100).trim() + " ";
    }
    if (number % 100 > 0) {
      result += numToWords(number % 100);
    }
    return result.trim() || "शून्य";
  };

  const handlePrintNotice = () => {
    setIsLoading(true);

    try {
      const printWindow = window.open('', '_blank');

      // Get the content from ref
      const printContent = printRef.current.innerHTML;

      const printStyles = `
        <style>
          @page { size: A4; margin: 10mm; }
          body { 
            font-family: 'Noto Sans Devanagari', Arial, sans-serif; 
            line-height: 1.6; margin: 0; padding: 20px; 
            background-color: white; font-size: 14px; 
          }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; border: 1px solid #000; }
          table td, table th { border: 1px solid #000; padding: 8px; vertical-align: top; }
          table, table td, table th {
  border: 1px solid #000 !important;  
}
          .bg-gray-50 { background-color: #f9f9f9; }
          .bg-yellow-50 { background-color: #fffbeb; }
          .bg-green-50 { background-color: #f0fdf4; }
          .bg-blue-50 { background-color: #eff6ff; }
          .font-bold { font-weight: bold; }
          .text-center { text-align: center; }
          .text-left { text-align: left; }
          .text-right { text-align: right; }
          .mb-4 { margin-bottom: 1rem; }
          .mb-6 { margin-bottom: 1.5rem; }
          .p-2 { padding: 0.5rem; }
          .p-3 { padding: 0.75rem; }
          .p-4 { padding: 1rem; }
          .leading-relaxed { line-height: 1.625; }
          .rounded-lg, .rounded { border-radius: 0.5rem; }
          .border { border: 1px solid #d1d5db; }
          .grid { display: grid; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
          .grid-cols-2, .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); } 
          .gap-3 { gap: 0.75rem; }
          .space-y-2 > * + * { margin-top: 0.5rem; }
          .space-y-3 > * + * { margin-top: 0.75rem; }
          .mt-8 { margin-top: 2rem; }
          .mt-16 { margin-top: 4rem; }
  
          .flex{ display:flex;}
          .justify-center {justify-content:center;}
}
        </style>
      `;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="hi">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>भूमि अधिग्रहण सूचना</title>
          ${printStyles}
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsLoading(false);
      }, 1000);

    } catch (error) {
      console.error('Error printing notice:', error);
      setIsLoading(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('hi-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  if (!isVisible) return null;

  const firstRecord = noticeData?.[0] || {};
  const isIndividual = noticeData.length === 1;

  // Edit
  // Handle input change for specific owner
  const handleScheduleChange = (ownerId, field, value) => {
    setScheduleData(prev =>
      prev.map(item =>
        item.land_owner_id === ownerId
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  // Save all schedule updates
  const handleSaveSchedules = async () => {
    setIsSavingSchedule(true);
    try {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const currentTime = now.toTimeString().split(' ')[0].slice(0, 5); // HH:MM

      // Prepare all promises
      const updatePromises = scheduleData.map(owner => {
        const payload = {
          scheduled_date: owner.scheduled_date || currentDate,
          scheduled_time: owner.scheduled_time || currentTime
        };
        return apiService.patchUpdateOwnerCompensationNotice(owner.land_owner_id, payload);
      });

      // Wait for all API calls to complete
      await Promise.all(updatePromises);

      alert('Schedule updated successfully for all owners!');
      setIsScheduleModalOpen(false);
      fetchData();

    } catch (error) {
      console.error('Error updating schedules:', error);
      alert('Failed to update schedules. Please try again.');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  // Render Schedule Modal
  const renderScheduleModal = () => {
    if (!isScheduleModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="border-b p-4 flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-800">Schedule Date & Time for Owners</h3>
            <button
              onClick={() => setIsScheduleModalOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="overflow-auto p-6 space-y-4">
            {scheduleData.map((owner, index) => (
              <div key={owner.land_owner_id} className="border rounded-lg p-4 bg-gray-50">
                {/* Owner Info */}
                <div className="mb-3 pb-3 border-b">
                  <p className="font-semibold text-gray-800">
                    {index + 1}. {owner.owner_name}
                  </p>
                  <p className="text-sm text-gray-600">Survey No: {owner.survey_no}</p>
                </div>

                {/* Date and Time Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scheduled Date
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(owner.scheduled_date)}
                      onChange={(e) => handleScheduleChange(owner.land_owner_id, 'scheduled_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Time Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scheduled Time
                    </label>
                    <input
                      type="time"
                      value={owner.scheduled_time}
                      onChange={(e) => handleScheduleChange(owner.land_owner_id, 'scheduled_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Modal Footer */}
          <div className="border-t p-4 flex justify-end space-x-3">
            <button
              onClick={() => setIsScheduleModalOpen(false)}
              disabled={isSavingSchedule}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSchedules}
              disabled={isSavingSchedule}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md transition-colors flex items-center space-x-2"
            >
              {isSavingSchedule && <Loader className="h-4 w-4 animate-spin" />}
              <span>{isSavingSchedule ? 'Saving...' : 'Save All'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoadingData) {
      return (
        <div className="flex flex-col items-center justify-center p-10">
          <Loader className="h-8 w-8 animate-spin text-blue-500" />
          <p className="mt-4 text-gray-600">Loading Notice Data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-10">
          <AlertTriangle className="h-8 w-8 text-red-500" />
          <p className="mt-4 text-red-600 font-semibold">Error Loading Data</p>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
        </div>
      );
    }

    if (noticeData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-10">
          <FileText className="h-8 w-8 text-gray-400" />
          <p className="mt-4 text-gray-600">No data available to generate the notice.</p>
        </div>
      );
    }

    // Render multiple notices (one per owner) when ownerId === 'all'
    return (
      <div ref={printRef}>
        {noticeData.map((ownerRecord, index) => (
          <React.Fragment key={index}>
            {renderSingleNotice(ownerRecord)}
            {/* Add page break after each notice except the last one */}
            {index < noticeData.length - 1 && (
              <div style={{ pageBreakAfter: 'always' }}></div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString) => {
    // If no date provided, use today
    if (!dateString) {
      const today = new Date();
      return today.toISOString().split('T')[0]; // yyyy-mm-dd
    }

    const date = new Date(dateString);

    // Check if date is invalid OR default 1900 date
    if (isNaN(date) || date.getFullYear() === 1900) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const renderSingleNotice = (ownerRecord) => {
    return (
      <div className="p-8 bg-white" id="notice-content">
        <div className="text-center mb-6">
          <p className="text-right mb-4 text-sm font-medium">निवाडा अ.क्र.</p>
          <p className="text-base font-bold mb-4">भूमिसंपादन, पुनर्वसन व पुनर्स्थापना करताना वाजवी भरपाई मिळण्याचा व पारदर्शकतेचा हक्क अधिनियम 2013</p>
          <p className="text-base font-bold mb-6">कलम 37 (2) खालील नोटीस</p>
          <table className="w-full border-collapse border-2 border-black mb-6">
            <tbody>
              <tr>
                <td className="border border-black p-3 font-bold bg-gray-50 w-1/3 text-left">प्रकल्प नाव :-</td>
                <td className="border border-black p-3 text-left">{currentProjectName}</td>
              </tr>
              <tr>
                <td className="border border-black p-3 font-bold bg-gray-50 text-left">भूमि संपादनाचे प्रयोजन :-</td>
                <td className="border border-black p-3 text-left">{proposalData.acquisition_purpose || ''}</td>
              </tr>
              <tr>
                <td className="border border-black p-3 font-bold bg-gray-50 text-left">भूसंपादन प्रकरण क्रमांक :-</td>
                <td className="border border-black p-3 text-left">{proposalData.section_11_bhusampadan_no || ''}</td>
              </tr>
              <tr>
                <td className="border border-black p-3 font-bold bg-gray-50 text-left">गावाचे नाव :-</td>
                <td className="border border-black p-3 text-left">
                  मौ.- {ownerRecord.village_name} ता. {ownerRecord.taluka_name} जि. {ownerRecord.dist_name}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="text-left mb-6">
          <p className="mb-4 font-medium">प्रति,</p>
          {/* Always show owner name for individual notices */}
          <p className="mb-4 font-medium">&emsp;&emsp;{ownerRecord.owner_name},</p>
          <p className="mb-6 leading-relaxed">
            &emsp;&emsp;याव्दारे नोटीस देण्यात येते की, आपणास ज्या वरील प्रकरणात हितसंबंधीत व्यक्ती म्हणून समजण्यात आले आहे. त्या वरील प्रकरणात, मी कथीत कायदयाच्या कलम 23 खालील तरतुदीनुसार दिनांक {currentDate} रोजी पुढीलप्रमाणे निवाडा केला आहे.
          </p>
          <table className="table-auto border-collapse border border-gray-400 w-full text-center">
            <tbody>
              <tr>
                <td className="border border-black p-2 text-center font-bold">गट क्रमांक</td>
                <td className="border border-black p-2 text-center font-bold">भूमिचे खरे एकुण क्षेत्रफळ (चौ.मी.)</td>
                <td className="border border-black p-2 text-center font-bold">भूमिसाठी देय भरपाई एकूण रक्कम रुपये</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center">{ownerRecord.survey_no}</td>
                <td className="border border-black p-2 text-center">{getCompensationValueForOwner(ownerRecord, 'totalAcquiredArea').toFixed(2)}</td>
                <td className="border border-black p-2 text-center">{formatAmount(getCompensationValueForOwner(ownerRecord, 'totalFinalCompensation'))}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="pb-2">1) सर्वसमावेशक मुल्यांकनाचा तपशिल खालीलप्रमाणे आहे (कलम 30 (2) नुसार) :-</p>

        <div className="no-page-break">
          <table className="w-full border-collapse border-2 border-black mb-6 text-sm">
            <tbody>
              <tr>
                <td className="border border-black p-2 text-center font-medium">01</td>
                <td className="border border-black p-3 text-left">कलम 26 (1) नुसार निर्धारित केलेले जमीनीचे बाजारमूल्य</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no1'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">02</td>
                <td className="border border-black p-3 text-left">ज्या घटकाव्दारे जमीनीचे बाजारमुल्य गुणावयाचे आहे तो घटक (गुणक)</td>
                <td className="border border-black p-2 text-center font-medium">{getCompensationValueForOwner(ownerRecord, 'landMultiplied')}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">03</td>
                <td className="border border-black p-3 text-left">जमीनीची किंमत (कलम 26 नुसार) (बाजारमुल्य गुणक या सुत्रानुसार) (रकाना 1x 2)</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'multipliedLandRate'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">04</td>
                <td className="border border-black p-3 text-left">फळझाडे / वनझाडे मुल्यांकन (कलम 27 नुसार)</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no4'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">05</td>
                <td className="border border-black p-3 text-left">विहीर / बोअरवेल / बांधकाम ई. चे मुल्यांकन (कलम 27 नुसार)</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no5'))}</td>
              </tr>
              <tr className="bg-yellow-50">
                <td className="border border-black p-2 text-center font-bold">06</td>
                <td className="border border-black p-3 text-left font-bold">एकुण भरपाई रक्कम (कलम 27 नुसार) (रकाना 3 ते 5 ची बेरीज )</td>
                <td className="border border-black p-2 text-center font-bold text-blue-600">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no6'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">07</td>
                <td className="border border-black p-3 text-left">दिलासा रक्कम @ 100% (कलम 30(1) नुसार)</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no6'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">08</td>
                <td className="border border-black p-3 text-left">जमीनीच्या बाजारमूल्यावर द.सा.द.शे. 12% या दराने अतिरिक्त घटकाची रक्कम (कलम 30 (3) नुसार)</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'bothInterestTotal'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">09</td>
                <td className="border border-black p-3 text-left">पाईपलाईनचे मुल्यांकन</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'pipelineCompensation'))}</td>
              </tr>
              <tr className="bg-green-50">
                <td className="border border-black p-2 text-center font-bold">10</td>
                <td className="border border-black p-3 text-left font-bold">निवाडयाची एकुण रक्कम (रकाना 6 ते 9 ची बेरीज)</td>
                <td className="border border-black p-2 text-center font-bold text-green-600">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no10'))}</td>
              </tr>
              <tr>
                <td className="border border-black p-2 text-center font-medium">11</td>
                <td className="border border-black p-3 text-left">वजा (-) 50% अग्रीम / अनर्जित । नजराणा रक्कमेची वसुली</td>
                <td className="border border-black p-2 text-center font-medium">{formatAmount(getCompensationValueForOwner(ownerRecord, 'no11'))}</td>
              </tr>
              <tr className="bg-blue-50">
                <td className="border border-black p-2 text-center font-bold">12</td>
                <td className="border border-black p-3 text-left font-bold">निव्वळ देय रक्कम (रकाना (9 + 10 - 11))</td>
                <td className="border border-black p-2 text-center font-bold text-blue-600">{formatAmount(getCompensationValueForOwner(ownerRecord, 'totalFinalCompensation'))}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="text-left mb-6 space-y-3">
          <p>2) भरपाई रक्कम पुढील प्रमाणे संविभाजित करण्यात आली आहे.</p>
          <div className="space-y-2 p-2">
            <div className="flex justify-center gap-16"><span>क _____________________________</span><span>यास रु. _____________________________</span></div>
            <div className="flex justify-center gap-16"><span>ख _____________________________</span><span>यास रु. _____________________________</span></div>
          </div>
          <p className="leading-relaxed">
            आपणास देय असलेली रक्कम रु. <strong>{formatAmount(getCompensationValueForOwner(ownerRecord, 'totalFinalCompensation'))}/- (अक्षरी {numberToMarathiWords(getCompensationValueForOwner(ownerRecord, 'totalFinalCompensation'))} रुपये)</strong> इतकी आहे. आपण व्यक्तीशः किंवा योग्यरीत्या प्राधिकृत केलेल्या अधिकर्त्यामार्फत दिनांक {formatDate(ownerRecord.scheduled_date)} रोजी उपजिल्हाधिकारी, भूसंपादन इमारत व दळणवळण विभाग जिल्हाधिकारी कार्यालय बुलढाणा ता. जि. बुलढाणा येथे सकाळी {ownerRecord.scheduled_time} वाजता माझ्‌यासमोर उपस्थित रहावे.
          </p>
          <p className="leading-relaxed">आपले अनुपस्थितीमुळे भरपाई रक्कमेचे प्रदान करण्यास विलंब झाल्यास, त्या कालावधीकरिता आपणास कोणतेही व्याज अनुज्ञेय राहणार नाही.</p>
          <div className="mt-8 space-y-2">
            <p><strong>दिनांक :-</strong> {currentDate}</p>
            <p><strong>ठिकाण :-</strong> {ownerRecord.dist_name || 'बुलढाणा'}.</p>
          </div>
        </div>

        <div className="text-right mt-16 space-y-2">
          <p className="font-medium">({usersLAO?.[0]?.full_name || 'N/A'})</p>
          <p className="font-bold">{usersLAO?.[0]?.designation || 'N/A'}</p>
          <p className="font-medium">(मध्यम प्रकल्प) {ownerRecord.dist_name || 'बुलढाणा'}</p>
        </div>

        <div className="pt-10 no-page-break">
          <p>टिप : येतांन खालील कागदपत्रे न चुकता सोबत आणावीत.</p>
          <div className="p-6 text-sm">
            <p>1) गाव नमुना 7/12 (प्रमाणित प्रत) (मालकी हक्काचा पुरावा) (अलीकडच्या दिनांकाचा)</p>
            <p>2) भूधारकाने आपली ओळख पटविण्यासाठी कोणतेही एक शासकीय मुळ ओळखपत्र उदा आधारकार्ड किंवा निवडणूक ओळखपत्र सोबत ठेवावे तसेच त्या ओळखपत्राची स्वयंसाक्षांकित फोटोपत दाखल करावी.</p>
            <p>3) राष्ट्रीयकृत बँकेचे प्रमाणपत्र (जोडपत्र-अ)</p>
            <p>4) राष्ट्रीयकृत बँकेच्या चालू स्थितीतील खाते पासबुकाची पहिल्या पानाची स्वयंसाक्षांकित स्वच्छ झेरॉक्सप्रत जनधन योजनेच्या खात्याला व्यवहारावर विशिष्ट रक्कमेची मर्यादा असल्याने जनधन योजनेचे पासबुक जोडण्यात येऊ नये.</p>
            <p>5) भूधारक मय्यत असल्यास, मृत्युचे मुळ प्रमाणपत्र / दाखला.</p>
            <p>6) सक्षम अधिकाऱ्यासमोरील कायदेशिर वारसाबाबत शपथपत्र (रु.100/- च्या मुद्रांकावर सर्व कायदेशीर वारसांचे)</p>
            <p>7) निवाड्यातील नाव, आधार कार्डवरील नाव, बैंक पासबुकावरील नाव यामध्ये काही बद / विसंगती दिसून येत असल्यास, तसेच स्पष्ट खुलासादर्शक शपथपत्र (रु.100/- च्या मुद्रांकावर सक्षम अधिकाऱ्यासमोरील).</p>
            <p>8) रु.1 चे रेव्हेन्यु स्टॅम्प (पावती तिकीट) प्रत्येक भूधारकासाठी 2 याप्रमाणे.</p>
            <p>9) भुधारक मय्यत असल्यास, सर्व कायदेशीर वारसांना ओळखपत्रासह हजर राहणे अनिवार्य आहे.</p>
            <p>10) संपादीत जमिनीच्या मालकी हक्कावर वित्तीय संस्थेने कर्जाचा बोजा निर्माण केला असल्यास, त्या संस्थेचा ना-देय / ना-हरकत दाखला.</p>
            <p>11) संपादीत मालमत्ता संस्थेच्या नावे असल्यास, संस्था धर्मदाय आयुक्त यांचेकडे पंजीबध्द केल्याबाबतचे नोंदणी प्रमाणपत्र, अनुसूची 1 व सर्व विश्वस्तांची अदयावत नावे व राहण्याच्या ठिकाणाचा संपूर्ण तपशिल.</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center shadow-sm z-10">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-800">
              भूमि अधिग्रहण सूचना - Section 37(2)
              {/* Show owner count when displaying multiple notices */}
              {ownerId === 'all' && noticeData.length > 0 && (
                <span className="text-sm font-normal text-blue-600 ml-2">
                  (Total {noticeData.length} Owner{noticeData.length > 1 ? 's' : ''})
                </span>
              )}
              {/* Show individual owner name when displaying single notice */}
              {ownerId !== 'all' && noticeData.length === 1 && (
                <span className="text-sm font-normal text-blue-600 ml-2">
                  ({firstRecord.owner_name})
                </span>
              )}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {/* {userRole === ROLES.LAO && ( */}
            {/* Data Entry */}
            {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
              <button
                onClick={() => setIsScheduleModalOpen(true)}
                disabled={isLoadingData || error || noticeData.length === 0}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-md text-sm font-medium transition-colors shadow-sm"
              >
                <FileText className="h-4 w-4" />
                <span>Schedule</span>
              </button>
            )}
            <button
              onClick={handlePrintNotice}
              disabled={isLoading || isLoadingData || error || noticeData.length === 0}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md text-sm font-medium transition-colors shadow-sm"
            >
              <Printer className="h-4 w-4" />
              <span>{isLoading ? 'Printing...' : 'Print'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
        <div className="overflow-auto">
          {renderContent()}
        </div>

        {renderScheduleModal()}
      </div>
    </div>
  );
};

export default CompensationNotices;